import { useCallback, useEffect, useState } from 'react';
import {
  formatPence,
  type HousesReply,
  type MarketHome,
  type RentalView,
  type RentalsReply,
} from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { api } from '../lib/api';
import { notify } from '../lib/alerts';
import { actionKey, londonWhen } from '../lib/format';
import { PhoneAppFrame, usePhoneApp, type PhoneAppProps } from '../lib/phone-app';

const area = (floor: { w: number; d: number }) => `${floor.w}×${floor.d} m`;

// Houses, as Lagos Life's 🔑 Houses: your home (rented or yours), buying it, extending it, selling it back, and
// the homes on the market to move to (rent with weeks up front and the agent, or buy outright)
export function HousesScreen(props: PhoneAppProps) {
  const { onWallet } = props;
  const { reply, failure, busy, load, run: act } = usePhoneApp(() => api.houses.market(), props);
  const [selling, setSelling] = useState(false);
  const [tab, setTab] = useState<'home' | 'let'>('home');
  const run = (work: () => Promise<HousesReply>) => act(work).finally(() => setSelling(false));
  const home = reply?.home;
  const wallet = reply?.walletPence ?? 0;
  const pay = (pence: number, label: string) => (wallet < pence ? `Need ${formatPence(pence)}` : label);
  return (
    <PhoneAppFrame
      id="houses"
      title="🔑 Houses"
      className="houses-screen"
      closeLabel="Close Houses"
      onClose={props.onClose}
      failure={failure}
      onRetry={() => void load()}
      loading={reply ? undefined : 'Calling the agent…'}
    >
      <div className="flights-tabs houses-tabs" role="tablist" aria-label="Houses">
        {(['home', 'let'] as const).map((item) => (
          <button
            type="button"
            key={item}
            role="tab"
            aria-selected={tab === item}
            className={tab === item ? 'active' : ''}
            onClick={() => setTab(item)}
          >
            {item === 'home' ? '🏠 My home' : '🏘️ To let'}
          </button>
        ))}
      </div>
      {tab === 'let' && <LetsTab onWallet={onWallet} />}
      {tab === 'home' && reply && home && (
        <div className="business-detail">
          <section className="houses-mine" aria-label="Your home">
            <span aria-hidden="true">{home.icon}</span>
            <span>
              <small>{home.owned ? 'YOURS' : 'YOU RENT'}</small>
              <strong>{home.label}</strong>
              <small>
                {home.area} · {area(home.room.floor)}
              </small>
            </span>
            <b>{home.owned ? 'No rent' : `${formatPence(home.rentPenceWeekly)}/wk`}</b>
          </section>
          {home.rent.owedPence > 0 && (
            <p className="business-warning">
              {formatPence(home.rent.owedPence)} rent owed. Pay it at home before moving or buying.
            </p>
          )}
          <section className="business-sums" aria-label="Your home's money">
            {home.owned ? (
              <>
                <p>
                  <span>Paid, with extensions</span>
                  <span>{formatPence(home.paidPence)}</span>
                </p>
                <p>
                  <span>Sells back for</span>
                  <span>{formatPence(home.sellPence)}</span>
                </p>
              </>
            ) : (
              <p>
                <span>Buy it outright</span>
                <span>{formatPence(home.buyPence)}</span>
              </p>
            )}
            <small>
              {home.owned
                ? `It's yours: no landlord, no agent, no rent. Extended ${home.extended} of ${home.extensions} times.`
                : `${reply.rules.buyWeeks} weeks of rent buys it: no rent, ever, and only a home you own can be extended.`}
            </small>
          </section>
          {!home.owned && (
            <button
              type="button"
              className="city-card-go"
              disabled={busy || wallet < home.buyPence || home.rent.owedPence > 0}
              onClick={() => void run(() => api.houses.act('buy', actionKey('house')))}
            >
              {pay(home.buyPence, `🔑 Buy it · ${formatPence(home.buyPence)}`)}
            </button>
          )}
          {home.owned && home.extendingUntil && (
            <p className="business-muted" role="status">
              🏗️ The crew is extending it. Ready {londonWhen(home.extendingUntil)}.
            </p>
          )}
          {home.owned && home.nextExtension && (
            <button
              type="button"
              className="city-card-go"
              disabled={busy || wallet < home.nextExtension.pricePence}
              onClick={() => void run(() => api.houses.act('extend', actionKey('house')))}
            >
              {pay(
                home.nextExtension.pricePence,
                `🏗️ Extend to ${area(home.nextExtension.floor)} · ${formatPence(home.nextExtension.pricePence)}`,
              )}
            </button>
          )}
          {home.owned && !home.extendingUntil && !home.nextExtension && (
            <p className="business-muted">🏠 Fully extended · {area(home.room.floor)}</p>
          )}
          {home.owned &&
            (selling ? (
              <div className="jet-sell">
                <span>Sell it back for {formatPence(home.sellPence)} and stay on as a tenant?</span>
                <button type="button" onClick={() => setSelling(false)} disabled={busy}>
                  Keep it
                </button>
                <button
                  type="button"
                  className="jet-sell-yes"
                  disabled={busy}
                  onClick={() => void run(() => api.houses.act('sell', actionKey('house')))}
                >
                  Sell
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="flights-terminal"
                disabled={Boolean(home.extendingUntil)}
                onClick={() => setSelling(true)}
              >
                Sell · {formatPence(home.sellPence)}
              </button>
            ))}
          <strong className="business-step-title">FOUND A BETTER PLACE?</strong>
          <p className="business-muted">
            Renting: {reply.rules.upfrontWeeks} weeks&apos; rent up front and the agent&apos;s fee. Your furniture comes
            with you; what doesn&apos;t fit is put away.
            {home.owned ? ' Sell your home first to move.' : ''}
          </p>
          <ul className="houses-market" aria-label="Homes on the market">
            {reply.market.map((item) => (
              <MarketCard
                key={item.id}
                item={item}
                wallet={wallet}
                busy={busy}
                blocked={home.owned || home.rent.owedPence > 0}
                onMove={(buy) => void run(() => api.houses.move({ kind: item.id, buy }, actionKey('move')))}
              />
            ))}
          </ul>
        </div>
      )}
    </PhoneAppFrame>
  );
}

function MarketCard({
  item,
  wallet,
  busy,
  blocked,
  onMove,
}: {
  item: MarketHome;
  wallet: number;
  busy: boolean;
  blocked: boolean;
  onMove: (buy: boolean) => void;
}) {
  return (
    <li className={item.current ? 'current' : ''}>
      <span className="business-icon" aria-hidden="true">
        {item.icon}
      </span>
      <span className="business-who">
        <strong>{item.label}</strong>
        <small>
          {item.area} · {area(item.floor)} · {item.guests} guests
        </small>
        <small>{item.description}</small>
      </span>
      <b>{formatPence(item.rentPenceWeekly)}/wk</b>
      {item.current ? (
        <em className="business-status">You live here</em>
      ) : (
        <div className="business-chips">
          <button type="button" disabled={busy || blocked || wallet < item.moveInPence} onClick={() => onMove(false)}>
            {wallet < item.moveInPence
              ? `Need ${formatPence(item.moveInPence)}`
              : `Rent it · ${formatPence(item.moveInPence)}`}
          </button>
          <button type="button" disabled={busy || blocked || wallet < item.buyPence} onClick={() => onMove(true)}>
            {wallet < item.buyPence
              ? `Buy · ${formatPence(item.buyPence)}`
              : `Buy & move in · ${formatPence(item.buyPence)}`}
          </button>
        </div>
      )}
    </li>
  );
}

// buy to let (as Lagos Life's Invest → houses): your homes to let, the rent you ask and how often the agent lets
// them, and the homes you can buy to let
function LetsTab({ onWallet }: { onWallet: () => void }) {
  const [reply, setReply] = useState<RentalsReply | null>(null);
  const [busy, setBusy] = useState(false);
  const run = useCallback(
    async (work: () => Promise<RentalsReply>) => {
      setBusy(true);
      try {
        const next = await work();
        setReply(next);
        if (next.message) {
          notify(next.message);
          onWallet();
        }
      } catch (error) {
        notify(describeError(error).message);
      } finally {
        setBusy(false);
      }
    },
    [onWallet],
  );
  useEffect(() => {
    void run(() => api.houses.lets());
  }, [run]);
  if (!reply) return <p className="business-muted">Calling the agent…</p>;
  const full = reply.rentals.length >= reply.maxProperties;
  return (
    <div className="business-detail">
      <p className="business-muted">
        Buy homes and let them: the agent finds tenants every evening from 6 PM and the rent comes to your wallet. Ask
        less and they are always let; ask more and fewer evenings are. {reply.rentals.length} of {reply.maxProperties}{' '}
        homes.
      </p>
      {reply.rentals.length > 0 && (
        <ul className="houses-market houses-lets" aria-label="Your homes to let">
          {reply.rentals.map((item) => (
            <LetCard key={item.id} item={item} busy={busy} run={run} />
          ))}
        </ul>
      )}
      <strong className="business-step-title">BUY A HOME TO LET</strong>
      <ul className="houses-market" aria-label="Homes to buy to let">
        {reply.offers.map((offer) => (
          <li key={offer.kind}>
            <span className="business-icon" aria-hidden="true">
              {offer.icon}
            </span>
            <span className="business-who">
              <strong>{offer.label}</strong>
              <small>
                {offer.area} · lets for {formatPence(offer.goingPence)}/wk
              </small>
              <small>About {formatPence(offer.weekPence)} a week at the going rent</small>
            </span>
            <b>{formatPence(offer.pricePence)}</b>
            <div className="business-chips">
              <button
                type="button"
                disabled={busy || full || reply.walletPence < offer.pricePence}
                onClick={() => void run(() => api.houses.buyLet({ kind: offer.kind }, actionKey('let')))}
              >
                {full
                  ? `${reply.maxProperties} homes already`
                  : reply.walletPence < offer.pricePence
                    ? `Need ${formatPence(offer.pricePence)}`
                    : `Buy to let · ${formatPence(offer.pricePence)}`}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LetCard({
  item,
  busy,
  run,
}: {
  item: RentalView;
  busy: boolean;
  run: (work: () => Promise<RentalsReply>) => Promise<void>;
}) {
  const [asked, setAsked] = useState(item.askedPence);
  const [selling, setSelling] = useState(false);
  useEffect(() => setAsked(item.askedPence), [item.askedPence]);
  const step = Math.max(100, Math.round((item.band.going * 0.05) / 100) * 100);
  return (
    <li>
      <span className="business-icon" aria-hidden="true">
        {item.icon}
      </span>
      <span className="business-who">
        <strong>{item.label}</strong>
        <small>
          {item.area} · let {Math.round(item.occupancy * 100)}% of evenings · about {formatPence(item.eveningPence)} an
          evening
        </small>
        <small>Earned {formatPence(item.earnedPence)}</small>
      </span>
      <b>{formatPence(item.askedPence)}/wk</b>
      <div className="houses-let-rent">
        <span className="railco-stepper">
          <button
            type="button"
            aria-label="Ask less"
            disabled={busy || asked - step < item.band.low}
            onClick={() => setAsked(asked - step)}
          >
            −
          </button>
          <strong>{formatPence(asked)}</strong>
          <button
            type="button"
            aria-label="Ask more"
            disabled={busy || asked + step > item.band.high}
            onClick={() => setAsked(asked + step)}
          >
            +
          </button>
        </span>
        <small>
          Going rate {formatPence(item.band.going)} · {formatPence(item.band.low)}–{formatPence(item.band.high)}
        </small>
        <div className="business-chips">
          <button
            type="button"
            disabled={busy || asked === item.askedPence}
            onClick={() =>
              void run(() => api.houses.letAct(item.id, { action: 'rent', askedPence: asked }, actionKey('let')))
            }
          >
            Ask {formatPence(asked)}
          </button>
          {selling ? (
            <>
              <button type="button" onClick={() => setSelling(false)}>
                Keep it
              </button>
              <button
                type="button"
                className="active"
                disabled={busy}
                onClick={() => void run(() => api.houses.letAct(item.id, { action: 'sell' }, actionKey('let')))}
              >
                Sell · {formatPence(item.sellPence)}
              </button>
            </>
          ) : (
            <button type="button" onClick={() => setSelling(true)}>
              Sell…
            </button>
          )}
        </div>
      </div>
    </li>
  );
}
